import { useSelector, useDispatch } from 'react-redux';
import { useEffect, useRef } from 'react';
import HelpMenu from './HelpMenu';
import { helpContent } from './helpContent';
import { setHelpMenu, setProfile } from '../Redux/store';
import { supabase } from '../supabase/client';

/**
 * HelpMenuContent - Centralized component for managing help/welcome menus
 * 
 * This component handles:
 * 1. Auto-showing welcome messages on first visit (calendar_welcome, profile_welcome)
 * 2. Rendering the HelpMenu component with appropriate content based on helpMenu state
 * 3. Handling close actions that update both Redux (instant) and Supabase (background)
 * 4. Clearing stale helpMenu state that may persist across page refreshes
 */
export default function HelpMenuContent() {
  // Redux state selectors
  const helpMenu = useSelector((s) => s.ui.helpMenu); // Current active help menu key
  const user = useSelector((s) => s.user.profile); // Current user profile data
  const currentPage = useSelector((s) => s.ui.currentPage); // Current page name
  const dispatch = useDispatch();
  
  // Ref to prevent re-showing profile welcome when user edits their profile
  const hasShownProfileWelcome = useRef(false);

  /**
   * useEffect - Auto-show welcome messages
   * 
   * Shows welcome messages based on first_time_views flags in profile data.
   * - calendar_welcome: Shows on calendar page if not yet seen
   * - profile_welcome: Shows on profile page if not yet seen
   * - share_welcome: Shows when share overlay opens if not yet seen (handled in ShareCalendarOverlay)
   */
  useEffect(() => {
    const firstTimeViews = user?.first_time_views || {};
    const isFirstCalendarTime = !firstTimeViews.calendar_welcome;
    const isFirstProfileTime = !firstTimeViews.profile_welcome;

    // Only show if user data is loaded
    if (user) {
      // Show calendar welcome on calendar page if not yet seen
      if (currentPage === 'calendar' && isFirstCalendarTime) {
        dispatch(setHelpMenu('calendar_welcome'));
      }
      // Show profile welcome on profile page if not yet seen and not already shown this session
      else if (currentPage === 'profile' && isFirstProfileTime && !hasShownProfileWelcome.current) {
        hasShownProfileWelcome.current = true;
        dispatch(setHelpMenu('profile_welcome'));
      }
    }
  }, [user, currentPage]);

  // Return null if no help menu is active
  if (!helpMenu) {
    return null;
  }

  const content = helpContent[helpMenu];
  if (!content) {
    return null;
  }

  // If content is a function (like shareHelpContent), call it with user data
  const contentToRender = typeof content.content === 'function' ? content.content(user) : content.content;

  /**
   * handleHelpMenuClose - Handle close actions for different help menu types
   *
   * This function handles the close action for welcome messages with special logic:
   *
   * 1. For calendar_welcome: Update first_time_views.calendar_welcome in Redux (instant)
   *    and Supabase (background) to mark that user has seen the calendar welcome.
   *
   * 2. For profile_welcome: Just close the menu - do NOT update first_time_views.profile_welcome.
   *    profile_welcome should only be set when user actually completes their profile
   *    in ProfileEditor.js, not when they dismiss the welcome message.
   *
   * 3. Always close the help menu immediately by dispatching setHelpMenu(null).
   *
   * The Redux update happens synchronously for instant UI feedback, while Supabase
   * updates happen asynchronously in the background. This provides a snappy user
   * experience even if the database is slow.
   */
  const handleHelpMenuClose = (helpKey) => {
    const firstTimeViews = user?.first_time_views || {};

    // Handle calendar_welcome close - mark as completed
    if (helpKey === 'calendar_welcome' && !firstTimeViews.calendar_welcome) {
      const updatedFirstTimeViews = { ...firstTimeViews, calendar_welcome: true };
      // Update Redux immediately for instant UI feedback
      dispatch(setProfile({ ...user, first_time_views: updatedFirstTimeViews }));
      // Close menu immediately
      dispatch(setHelpMenu(null));
      // Update Supabase in background (fire and forget)
      supabase
        .from('profiles')
        .update({ first_time_views: updatedFirstTimeViews })
        .eq('id', user.id)
        .select()
        .then(({ data, error }) => {
          // console.log('[HelpMenuContent] calendar_welcome Supabase update:', { data, error });
        });
      return;
    }

    // Handle share_welcome close - mark as completed
    if (helpKey === 'share_welcome' && !firstTimeViews.share_welcome) {
      const updatedFirstTimeViews = { ...firstTimeViews, share_welcome: true };
      // Update Redux immediately for instant UI feedback
      dispatch(setProfile({ ...user, first_time_views: updatedFirstTimeViews }));
      // Close menu immediately
      dispatch(setHelpMenu(null));
      // Update Supabase in background (fire and forget)
      supabase
        .from('profiles')
        .update({ first_time_views: updatedFirstTimeViews })
        .eq('id', user.id)
        .select()
        .then(({ data, error }) => {
          // console.log('[HelpMenuContent] share_welcome Supabase update:', { data, error });
        });
      return;
    }

    // Handle profile_welcome close - just close, don't update first_time_views
    // profile_welcome is only set in ProfileEditor.js when user actually completes profile
    if (helpKey === 'profile_welcome') {
    }

    // Always close the help menu immediately
    dispatch(setHelpMenu(null));
  };

  return (
    <HelpMenu
      title={content.title}
      content={contentToRender}
      onClose={() => handleHelpMenuClose(helpMenu)}
      buttonText={content.buttonText}
    />
  );
}
